/**
 * Catalogue filtering — case-insensitive substring match on shell product
 * display name + description.
 *
 * v1 keeps it simple (no Fuse.js dependency). Filter composes:
 *   - If a search query is active, returns ALL matching products across
 *     categories (users expect cross-category results from a search).
 *   - If no search query, returns all products grouped by category (caller
 *     decides scroll behavior).
 *
 * Categories with zero products after filtering are dropped so the UI can
 * cleanly skip empty sections.
 */

import type { ResolvedCatalogue, ResolvedFeaturedEntry, ResolvedShellProduct } from 'models/purchaseFlowRevamp'

export interface FilteredCategory {
  id: string
  displayName: string
  displayOrder: number
  iconId?: string
  products: ResolvedShellProduct[]
}

export interface FilterResult {
  categories: FilteredCategory[]
  /**
   * The Featured carousel's cards (bundles and featured products), in order. While a search
   * is active it holds only the bundles that match, by their own name or by a member's name;
   * a featured product is already listed in its category.
   */
  featured: ResolvedFeaturedEntry[]
  totalProducts: number
  isSearchActive: boolean
}

const normalize = (value: string): string => value.toLowerCase().trim()

const matchesQuery = (product: ResolvedShellProduct, needle: string): boolean => {
  if (!needle) return true
  const haystack = `${product.displayName} ${product.description ?? ''}`.toLowerCase()
  return haystack.includes(needle)
}

/**
 * Filters a `ResolvedCatalogue` by an optional search query.
 *
 * Category filtering is intentionally NOT part of this hook — the UI uses a
 * scroll-anchor pattern (all sections rendered, active tab tracks viewport),
 * so scoping by category happens via the DOM, not the data.
 */
export function filterCatalogue(
  catalogue: ResolvedCatalogue | undefined,
  rawQuery: string
): FilterResult {
  if (!catalogue) {
    return { categories: [], featured: [], totalProducts: 0, isSearchActive: false }
  }

  const needle = normalize(rawQuery)
  const isSearchActive = needle.length > 0

  // Keyed on `tabId`, which is what a category's id now is — `categoryId` stays
  // the legacy product category and no longer names a tab.
  const productsByCategory = new Map<string, ResolvedShellProduct[]>()
  for (const cat of catalogue.categories) {
    productsByCategory.set(cat.id, [])
  }

  // A bundle lives in `shellProducts` so a cart line can resolve it, but it is
  // merchandised in the Featured carousel — bucketing it here as well would
  // render it twice.
  const bundleIds = new Set(catalogue.bundles.map((bundle) => bundle.id))

  for (const product of catalogue.shellProducts) {
    if (!product.active) continue
    if (bundleIds.has(product.id)) continue
    if (!matchesQuery(product, needle)) continue
    const bucket = productsByCategory.get(product.tabId)
    if (bucket) bucket.push(product)
  }

  // Stable sort by displayOrder within each category.
  for (const bucket of productsByCategory.values()) {
    bucket.sort((a, b) => a.displayOrder - b.displayOrder)
  }

  const categories: FilteredCategory[] = catalogue.categories
    .slice()
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((cat) => ({
      id: cat.id,
      displayName: cat.displayName,
      displayOrder: cat.displayOrder,
      iconId: cat.iconId,
      products: productsByCategory.get(cat.id) ?? [],
    }))
    .filter((cat) => cat.products.length > 0)

  const featured = catalogue.featured.filter(({ shell, variantId }) => {
    const variant = shell.variants.find((v) => v.id === variantId && v.active)
    if (!shell.active || !variant) return false
    if (!isSearchActive) return true
    if (!bundleIds.has(shell.id)) return false
    return matchesQuery(shell, needle)
      || (variant.bundledProducts ?? []).some((member) => member.displayName?.toLowerCase().includes(needle))
  })

  // Featured bundles count: a catalogue that offers nothing but packs is not empty, and the
  // caller uses this to decide between the grid and the empty state. A featured product is
  // already counted in its category.
  const totalProducts = categories.reduce((sum, c) => sum + c.products.length, 0)
    + featured.filter(({ shell }) => bundleIds.has(shell.id)).length

  return { categories, featured, totalProducts, isSearchActive }
}
